import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["trackingSource", "upgradeData", "offerProductConfigurations", "use", "data-selenium-id", "data-selenium", "data-test-id", "currencyCode", "location", "children", "block", "size", "requestedProducts", "terms"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { View } from 'foundations-components/transitional/components/View';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { REQUEST_ADDITIONAL_SEATS } from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import { TEAM } from 'ui-addon-upgrades/_core/common/constants/AdminTypes';
import * as tracker from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import UICheckoutButton from 'ui-addon-upgrades/button/UICheckoutButton';
import UIAdminOnlyWrapper from 'ui-addon-upgrades/decorators/UIAdminOnlyWrapper';
import { omit } from '../../utils';
import { upgradeDataPropsInterface } from '../common/data/upgradeData/interfaces/upgradeDataPropsInterface';
import { getCanViewNewSeats } from '../common/eligibility/getCanViewNewSeats';
import { BUY_ADDITIONAL_SEATS } from '../purchaseMotions/PurchaseMotionTypes';
import { trackPurchaseMotionFailure } from '../utils/metricUtils';
import { AddAdditionalSeatsPropsInterface } from './AddAdditionalSeatsPropsInterface';

// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AddAdditionalSeatsButton = _ref => {
  let {
      trackingSource,
      upgradeData,
      offerProductConfigurations = [],
      use = 'secondary',
      'data-selenium-id': dataSeleniumId = 'add-additional-seats-button',
      'data-selenium': dataSelenium = 'add-additional-seats-button',
      'data-test-id': dataTestId,
      currencyCode,
      location,
      children,
      block,
      size,
      requestedProducts = [],
      terms
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [hasSeatsAccess, setHasSeatsAccess] = useState(rest.canViewNewSeats);
  useEffect(() => {
    if (rest.canViewNewSeats === undefined) {
      void getCanViewNewSeats().then(setHasSeatsAccess);
    } else {
      setHasSeatsAccess(rest.canViewNewSeats);
    }
  }, [rest.canViewNewSeats]);
  const buttonCopy = useMemo(() => children || (hasSeatsAccess ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "upgrades.pricingCtas.buyAdditionalSeats"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "upgrades.pricingCtas.buyAdditionalUsers"
  })), [children, hasSeatsAccess]);
  const handleClick = isValid => {
    tracker.track('addAdditionalSeatsClick', {
      upgradeProduct: upgradeData.upgradeProduct,
      apiName: upgradeData.apiName,
      trackingSource,
      isValid
    });
    if (!isValid) {
      trackPurchaseMotionFailure({
        uniqueId: upgradeData.uniqueId,
        apiName: upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct],
        purchaseMotion: BUY_ADDITIONAL_SEATS
      });
      setIsErrorVisible(true);
    }
  };
  const restWithUpgradeData = Object.assign({}, rest, {
    upgradeData
  });
  const propsToPass = omit(restWithUpgradeData, ['isUpgradePoint', 'trackingSource', 'upgradeData', 'currencyCode', 'canViewNewSeats']);
  const isValid = requestedProducts.length > 0 ? requestedProducts.some(item => {
    var _item$quantity;
    return ((_item$quantity = item === null || item === void 0 ? void 0 : item.quantity) !== null && _item$quantity !== void 0 ? _item$quantity : 0) > 0;
  }) : offerProductConfigurations === null || offerProductConfigurations === void 0 ? void 0 : offerProductConfigurations.some(item => item.productConfigurations.some(config => config.quantity > 0));
  if (!isValid) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: /*#__PURE__*/_jsx(UIAdminOnlyWrapper, {
        adminType: TEAM,
        modalType: REQUEST_ADDITIONAL_SEATS,
        upgradeData: upgradeData,
        children: /*#__PURE__*/_jsxs("div", {
          children: [isErrorVisible && /*#__PURE__*/_jsx(View, {
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(Alert, {
              type: "danger",
              "data-selenium-id": `error-${dataSeleniumId}`,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "upgrades.pricingCtas.addUsersError"
              })
            })
          }), /*#__PURE__*/_jsx(Button, {
            use: use,
            block: block,
            size: size,
            "data-selenium-id": `dummy-${dataSeleniumId}`,
            onClick: () => handleClick(false),
            children: buttonCopy
          })]
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(UIAdminOnlyWrapper, {
    adminType: TEAM,
    modalType: REQUEST_ADDITIONAL_SEATS,
    upgradeData: upgradeData,
    children: /*#__PURE__*/_jsx(UICheckoutButton, Object.assign({
      "data-selenium-id": dataSeleniumId,
      "data-selenium": dataSelenium,
      "data-test-id": dataTestId,
      truncate: true,
      onClick: () => handleClick(true),
      disabled: !isValid,
      offerProductConfigurations: offerProductConfigurations,
      use: use,
      upgradeData: upgradeData,
      currencyCode: currencyCode,
      location: location,
      trackingSource: trackingSource,
      block: block,
      size: size,
      requestedProducts: requestedProducts,
      terms: terms
    }, propsToPass, {
      children: buttonCopy
    }))
  });
};
AddAdditionalSeatsButton.propTypes = Object.assign({}, upgradeDataPropsInterface, AddAdditionalSeatsPropsInterface, {
  children: PropTypes.node
});
export default AddAdditionalSeatsButton;